import { Box } from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { search, setQuery } from 'store/person/lookup'
import { getClients } from 'store/lookup'
import { capitalize } from 'lodash'
import NoRecords from 'components/NoRecords'
import Filters from 'components/Filters'
import { ROWS_PER_PAGE_OPTIONS, USER_TYPE_OPTIONS } from 'constants'

const DEFAULT_LIMIT = 10

const filtersConfig = [
  { key: 'que', label: 'Search', type: 'text', gridProps: { xs: 12, sm: 6, md: 4 }, size: 'medium' },
  { key: 'type', label: 'Type', type: 'autocomplete', options: USER_TYPE_OPTIONS, gridProps: { xs: 12, sm: 6, md: 2 }, size: 'medium' },
  { key: 'districtId', label: 'District', type: 'autocomplete', gridProps: { xs: 12, sm: 6, md: 3 }, size: 'medium' },
  { key: 'campusId', label: 'Campus', type: 'autocomplete', gridProps: { xs: 12, sm: 6, md: 3 }, size: 'medium' },
]

export default function LookupList({ onRowClick }) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const list = useSelector(state => state.personLookup.list)
  const count = useSelector(state => state.personLookup.count)
  const loading = useSelector(state => state.personLookup.loading)
  const query = useSelector(state => state.personLookup.query)
  const clients = useSelector(state => state.lookup.clients)
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_LIMIT })

  useEffect(() => {
    if (!clients) dispatch(getClients())
  }, [clients])

  const districtOptions = clients?.map(c => ({ value: c.client?.id, label: c.client?.name })) ?? []
  const selectedClient = clients?.find(c => c.client?.id === query.districtId)
  const campusOptions = (selectedClient ? [selectedClient] : clients ?? [])
    .flatMap(c => (c.client?.schools ?? []).flatMap(s => (s.campuses ?? []).map(campus => ({ value: campus.id, label: campus.name }))))

  const handlePaginationChange = model => {
    setPaginationModel(model)
    dispatch(search({ ...query, limit: model.pageSize, offset: model.page * model.pageSize }))
  }

  const handleSortChange = sortModel => {
    const sort = sortModel[0]
    const newQuery = { ...query }
    if (sort?.field) {
      newQuery.sortBy = sort.field
      newQuery.sortOrder = sort.sort
    } else {
      delete newQuery.sortBy
      delete newQuery.sortOrder
    }
    dispatch(setQuery(newQuery))
    dispatch(search(newQuery))
  }

  const columns = [
    {
      field: 'lastName',
      headerName: t('Last Name'),
      flex: 1,
      renderCell: params => params.row?.lastName,
    },
    {
      field: 'firstName',
      headerName: t('First Name'),
      flex: 1,
      renderCell: params => params.row?.firstName,
    },
    { field: 'type', headerName: t('Type'), flex: 1, renderCell: ({ row }) => `${capitalize(row.type)}` },
    { field: 'campus', headerName: t('Campus'), flex: 1.5, renderCell: ({ row }) => `${row.personAccessViews?.[0]?.campusName || '—'}`, sortable: false },
    { field: 'email', headerName: t('Email'), flex: 1, renderCell: ({ row }) => `${row.email || '—'}`, sortable: false },
  ]

  return (
    <Box pt={2} px={1}>
      <Filters
        filtersConfig={filtersConfig}
        optionsMap={{ districtId: districtOptions, campusId: campusOptions }}
        api={{
          search,
          setQuery,
          querySelector: state => state.personLookup.query,
        }}
        defaultQuery={{ limit: DEFAULT_LIMIT, offset: 0 }}
        gridContainerProps={{ sx: { mb: 1, px: 0 } }}
      />
        <DataGrid
          rows={list ?? []}
          columns={columns}
          getRowId={row => row.id}
          disableColumnMenu
          loading={loading}
          onRowClick={params => onRowClick?.(params.row)}
          sortingMode="server"
          onSortModelChange={handleSortChange}
          paginationMode="server"
          rowCount={count ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{
            height: 'calc(100vh - 295px) !important',
            overflow: 'auto',
            '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', py: 6 }}>
                <NoRecords />
              </Box>
            ),
          }}
        />
    </Box>
  )
}
